/**
 * Drop-in replacement for `React.lazy` that auto-recovers from the
 * stale-chunk failure mode every Vite/Rollup SPA suffers from.
 *
 * THE BUG (every prod SPA hits this exactly once per non-trivial
 * deploy, every time):
 *
 *   1. User loads the page at 09:00 — browser caches `index.html`
 *      which references `BrowsePage-BrmUa2Em.js`.
 *   2. We deploy at 09:01 — the new build emits
 *      `BrowsePage-D3g4vX8p.js` (different content hash) and the OLD
 *      chunk is purged from Netlify's edge.
 *   3. User clicks "Browse" at 09:02 — React.lazy fires
 *      `import('/assets/BrowsePage-BrmUa2Em.js')`, the CDN returns
 *      404, the dynamic import promise rejects, react-router catches
 *      it as a render-time crash and shows
 *      `"Unexpected Application Error! Failed to fetch dynamically
 *      imported module: ..."`. Whitescreen, no recovery.
 *
 * THE FIX (this file):
 *
 *   - On dynamic-import failure that matches the known cross-browser
 *     "chunk load failed" signatures, we set a sessionStorage flag and
 *     call `window.location.reload()`.
 *   - The reload picks up the new `index.html` from Netlify which
 *     references the new chunk hash, the import succeeds, and the
 *     user lands exactly where they wanted to go.
 *   - To avoid an infinite reload loop if the new deploy is itself
 *     genuinely broken, we gate on a 10-second sessionStorage
 *     debounce: at most one auto-reload per browser tab per 10s. If
 *     we hit the debounce we propagate the error so the user lands
 *     on the friendly `<ErrorFallback>` screen with a reference code
 *     instead of an infinite spinner.
 *   - While the reload is pending we return a never-resolving
 *     promise. React keeps showing the parent Suspense boundary
 *     instead of trying to render the error mid-reload (the user
 *     would otherwise see a 100ms flash of red error UI).
 *
 * Use everywhere you would have used `React.lazy`:
 *
 *   const BrowsePage = lazyWithReload(() => import('./pages/BrowsePage'));
 */

import { lazy, type ComponentType, type LazyExoticComponent } from 'react';

const RELOAD_DEBOUNCE_MS = 10_000;
const RELOAD_STORAGE_KEY = 'pc.chunkReloadAt';

/**
 * Cross-browser detection for "this import 404'd because the chunk is
 * gone from the CDN", as opposed to "this code legitimately threw".
 *
 *   Chrome   : "Failed to fetch dynamically imported module"
 *   Firefox  : "error loading dynamically imported module" /
 *              "Loading chunk \d+ failed"
 *   Safari   : "Importing a module script failed" /
 *              "Unable to preload CSS"
 *   Generic  : `ChunkLoadError` (rollup runtime), 4xx fetch error
 *              messages from native `fetch`.
 *
 * We err generous: any 4xx/5xx network error during a dynamic import
 * almost certainly means a stale shell. False positives reload the
 * page, which is benign; false negatives strand the user on a broken
 * screen, which is what we are trying to eliminate.
 */
export function isChunkLoadError(err: unknown): boolean {
  if (!err) return false;
  const msg =
    err instanceof Error
      ? `${err.name}: ${err.message}`
      : typeof err === 'string'
        ? err
        : '';
  return (
    /Loading chunk \d+ failed/i.test(msg) ||
    /Failed to fetch dynamically imported module/i.test(msg) ||
    /error loading dynamically imported module/i.test(msg) ||
    /Importing a module script failed/i.test(msg) ||
    /Unable to preload CSS/i.test(msg) ||
    /\bChunkLoadError\b/.test(msg) ||
    /dynamically imported module .* not found/i.test(msg)
  );
}

/**
 * Returns true once a reload has been queued and the caller should
 * suspend / no-op instead of propagating an error. Updates the
 * sessionStorage debounce timestamp on the way out. Returns false if
 * we've already reloaded within `RELOAD_DEBOUNCE_MS` — in that case
 * the caller should let the error propagate to the boundary so the
 * user lands on a friendly screen instead of an infinite spinner.
 */
export function scheduleChunkReload(): boolean {
  if (typeof window === 'undefined') return false;
  let last = 0;
  try {
    last = Number(window.sessionStorage.getItem(RELOAD_STORAGE_KEY) || 0);
  } catch {
    // sessionStorage can throw in private mode / Safari quotas;
    // treat as "first reload" and proceed.
  }
  if (Number.isFinite(last) && Date.now() - last < RELOAD_DEBOUNCE_MS) {
    return false;
  }
  try {
    window.sessionStorage.setItem(RELOAD_STORAGE_KEY, String(Date.now()));
  } catch {
    // Same swallow as above; the reload is still safe to do.
  }
   
  console.warn(
    '[pc/lazyWithReload] stale chunk detected; reloading to pick up the latest deploy.',
  );
  window.location.reload();
  return true;
}

/**
 * Same shape as `React.lazy`. Use it for every code-split route /
 * heavy widget so the user never sees a stale-chunk crash.
 *
 * Why we *don't* retry the original import once before reloading:
 * the chunk is GONE from the CDN; a retry will 404 again with
 * identical odds. A hard reload is the only correct recovery, and
 * the soonest we do it the better the user experience.
 */
export function lazyWithReload<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
): LazyExoticComponent<T> {
  return lazy(async () => {
    try {
      return await factory();
    } catch (err) {
      if (isChunkLoadError(err)) {
        const reloading = scheduleChunkReload();
        if (reloading) {
          // Suspend forever so React keeps the Suspense fallback up
          // until the reload yanks the page. Throwing here would let
          // the error boundary paint for a frame, which is exactly
          // the UX we are trying to delete.
          return new Promise<{ default: T }>(() => {});
        }
      }
      throw err;
    }
  });
}
